import { useEffect, useRef, useState } from 'react';
import { Modal, ModalHeader, Button } from './ui';
import { DLG } from './dialogStyles';
import { classDataChanged, teacherApi } from '../data/api';
import { dateLabel } from '../data/teacherHomeModel';
import { isAssessment } from '../data/assessments';

/* Finish a homework from Buffy's chat (2026-10-06). Same call and same words as
   Assignments → Review and finish homework (closeHomework, 'Finish "X"?',
   Finish homework / Keep it open), including the page's rule that an
   uncertain failure must be reloaded before trying again. Buffy may name the
   homework by id or title; otherwise the teacher picks from the open ones.
   A curriculum assessment is named as one, never as practice (2026-10-18). */
// The page's words (AssignmentsPage): a practice set or an assessment.
const setNoun = (hw) => (isAssessment(hw) ? 'assessment' : 'practice set');
export default function FinishHomeworkDialog({ params, auth, classId, onClose, onDone }) {
  const [open, setOpen] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const writing = useRef(false);
  useEffect(() => {
    let alive = true;
    teacherApi(auth, 'listHomework', { classID: classId })
      .then((r) => {
        if (!alive) return;
        const items = (r?.items || []).filter((hw) => hw.status === 'open');
        setOpen(items);
        const wanted = String(params.title || '').trim().toLowerCase();
        const hit = items.find((hw) => (params.homework && hw.hwId === params.homework) || (wanted && String(hw.title || '').trim().toLowerCase() === wanted));
        if (hit) setTarget(hit);
        else if (items.length === 1 && !params.homework && !wanted) setTarget(items[0]);
      })
      .catch(() => { if (alive) setLoadError(true); });
    return () => { alive = false; };
  }, [auth, classId, params.homework, params.title]);

  const finish = async () => {
    if (!target || writing.current || uncertain) return;
    writing.current = true; setBusy(true); setError('');
    try {
      await teacherApi(auth, 'closeHomework', { classID: classId, hwId: target.hwId });
      classDataChanged(classId);
      onDone(`Finished the ${setNoun(target)} "${target.title}".`, `Finished “${target.title}”`);
      onClose();
    } catch (err) {
      const ambiguous = !(err.status >= 400 && err.status < 500 && err.status !== 408);
      setUncertain(ambiguous);
      setError(ambiguous ? `We couldn’t confirm whether this ${setNoun(target)} finished. Reload before trying again.` : err.message || `Couldn’t finish this ${setNoun(target)}. Try again.`);
    } finally { writing.current = false; setBusy(false); }
  };

  if (!target) {
    return (
      <Modal open onClose={onClose} size="sm">
        <ModalHeader title="Finish a practice set" sub="Pick one that is open" pad={DLG.head} onClose={onClose} />
        <div className={`${DLG.body} flex flex-col gap-[8px]`}>
          {loadError && <p className="m-0 text-[13px] text-error-600">Homework updates are unavailable. Your existing assignments have not changed.</p>}
          {!loadError && !open && <p className="m-0 text-[13px] text-neutral-500">Loading assignments…</p>}
          {open && !open.length && <p className="m-0 text-[13px] text-neutral-500">There are no open assignments in this class.</p>}
          {(open || []).map((hw) => (
            <button
              key={hw.hwId}
              type="button"
              onClick={() => setTarget(hw)}
              className={DLG.row}
            >
              <span className="block text-[14px] font-semibold text-ink">{hw.title}</span>
              {(isAssessment(hw) || hw.dueDate) && <span className="block text-[12px] text-neutral-500">{[isAssessment(hw) && 'Assessment', hw.dueDate && `Due ${dateLabel(hw.dueDate)}`].filter(Boolean).join(' · ')}</span>}
            </button>
          ))}
        </div>
      </Modal>
    );
  }
  return (
    <Modal open onClose={busy ? () => {} : onClose} size="sm">
      <ModalHeader title={`Finish “${target.title}”?`} pad={DLG.head} onClose={busy ? () => {} : onClose} />
      <div className={DLG.body}>
        <p className="m-0 text-[13px] text-neutral-700 leading-relaxed">This ends the {setNoun(target)}. Students keep their learning progress and can continue practicing independently.</p>
        {error && <p role="alert" className="mt-[12px] mb-0 text-[12.5px] font-semibold text-error-600">{error}</p>}
      </div>
      <div className={DLG.foot}>
        {uncertain && <Button variant="secondary" size="dlg" onClick={() => window.location.reload()}>Reload assignments</Button>}
        <Button variant="secondary" size="dlg" onClick={onClose} disabled={busy}>Keep it open</Button>
        <Button variant="primary" size="dlg" onClick={finish} disabled={busy || uncertain}>{busy ? 'Finishing…' : 'Yes, finish it'}</Button>
      </div>
    </Modal>
  );
}
